import { RootState } from 'duck/store'
import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import OrganizationDetailsModal, {
  OrganizationDetailsModalFooter,
} from 'scenes/Dashboard/components/OrganizationDetailsModal'
import ReanalyzeButton from 'scenes/Dashboard/components/ReanalyzeButton'
import { ResetFreshnessButton } from 'scenes/Dashboard/components/ResetFreshnessButton'
import SectionLoading from 'scenes/Dashboard/components/SectionLoading'
import { getIsFetchingInvestorChecks } from 'scenes/Dashboard/data/investorChecks/selectors'
import { makeStyles } from 'tss-react/mui'

import { Fade, Theme, Typography } from '@mui/material'

import NewPairingModal from './modals/NewPairingModel.component'

import { DealSearchButton, DealStack, PairingActions, PairingFilters } from './components'
import { loadData, unMountPairingManager } from './duck/actions'
import { getIsFetchingDeals } from './duck/selectors'

export const PairingManager = React.memo(() => {
  const { classes } = useStyles()
  const { isLoading } = usePairingManagerData()
  usePairingManagerEffects()

  const [isOpenedNewPairing, setIsOpenNewPairing] = useState(false)

  const organizationActionsSection: OrganizationDetailsModalFooter = useCallback(
    ({ organization, deal }) => {
      if (!deal) {
        return null
      }
      return (
        <div className={classes.modalFooter}>
          <div className={classes.specialModalButtons}>
            <ReanalyzeButton className={classes.adminButton} dealId={deal.id} isAdmin />
            <ResetFreshnessButton className={classes.adminButton} dealId={deal.id} />
          </div>
          <PairingActions deal={deal!} organization={organization} />
        </div>
      )
    },
    [classes.adminButton, classes.modalFooter, classes.specialModalButtons]
  )
  return (
    <Fade in={true}>
      <div className={classes.pairingManager}>
        <div className={classes.headerContainer}>
          <Typography className={classes.headline}>Pairings Manager</Typography>
          <PairingFilters />
        </div>
        <div className={classes.pairingManagerActions}>
          {isOpenedNewPairing && <NewPairingModal onClose={() => setIsOpenNewPairing(false)} />}
          <DealSearchButton className={classes.pairingManagerButton} disabled={isLoading} />
        </div>
        <div className={classes.content}>
          {isLoading ? <SectionLoading className={classes.loading} /> : <DealStack />}
        </div>

        <OrganizationDetailsModal footer={organizationActionsSection} />
      </div>
    </Fade>
  )
})

PairingManager.displayName = 'PairingManager'

function usePairingManagerData() {
  const isFetching = useSelector((state: RootState) => getIsFetchingDeals(state))
  const isFetchingChecks = useSelector((state: RootState) => getIsFetchingInvestorChecks(state))
  const dispatch = useDispatch()
  useEffect(() => {
    dispatch(loadData())
    return () => {
      dispatch(unMountPairingManager())
    }
  }, [dispatch])

  return {
    isLoading: isFetching || isFetchingChecks,
  }
}

function usePairingManagerEffects() {
  const dispatch = useDispatch()
  useEffect(() => {
    return () => {
      dispatch(unMountPairingManager())
    }
  }, [dispatch])
}

const useStyles = makeStyles()(({ spacing, typography, breakpoints }: Theme) => ({
  pairingManager: {
    display: 'flex',
    width: '100%',
    height: '100%',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing(2),
  },
  headline: {
    flex: '0 0 auto',
    display: 'inline-block',
    fontSize: typography.h5.fontSize,
    marginRight: spacing(2),
    [breakpoints.down('sm')]: {
      fontSize: typography.subtitle1.fontSize,
    },
  },
  pairingManagerActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: spacing(3),
  },
  pairingManagerButton: {
    marginLeft: 'auto',
  },
  content: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    minHeight: spacing(50),
    flex: '1 1 auto',
  },
  loading: {
    position: 'absolute',
    zIndex: 2,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
  },
  modalFooter: {
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  specialModalButtons: {
    width: '100%',
    marginBottom: spacing(),
  },
  adminButton: {
    marginRight: spacing(0.5),
    marginLeft: spacing(0.5),
  },
}))
